import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  AppBadge,
  AppBadgeForID,
  AppIconButton,
  AppMemberDisplay,
  AppSelectPopover,
  AppTooltip,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Link } from '@tanstack/react-router'
import type { OpportunityTask, OpportunityTaskStatus } from 'backend/types'
import { Pencil, Trash2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'
import { formatDate, toDateTime } from '@/lib/format-utils'

/**
 * テキストを切り詰める
 */
function truncateText(text: string | null, maxLength = 50): string {
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

/**
 * 日本語形式の日付文字列に変換
 */
function toDateStringJP(date: string | null): string {
  if (!date) return '-'
  const dt = DateTime.fromISO(date)
  if (!dt.isValid) return '-'
  return dt.toFormat('yyyy年MM月dd日')
}

/**
 * ステータスIDからステータス情報を取得
 */
function getTaskStatus(statusId: number, statuses: OpportunityTaskStatus[]): OpportunityTaskStatus | undefined {
  return statuses.find((s) => s.id === statusId)
}

/**
 * タスク一覧のカラム定義を生成
 *
 * @param statuses - タスクステータス一覧
 * @param onStatusChange - ステータス変更ハンドラー
 * @param onEdit - 編集ハンドラー
 * @param onDelete - 削除ハンドラー
 */
export function createTaskColumns(params: {
  statuses: OpportunityTaskStatus[]
  onStatusChange: (task: OpportunityTask, newStatusId: number) => void
  onEdit: (task: OpportunityTask) => void
  onDelete: (task: OpportunityTask) => void
}): AppDataTableColumn<OpportunityTask>[] {
  const { statuses, onStatusChange, onEdit, onDelete } = params

  return [
    // 1. タイトル（required: true, active: true）
    {
      key: 'title',
      label: 'タイトル',
      accessor: (row) => row.title,
      cellClassName: 'w-[120px]',
      headerClassName: 'w-[120px]',
      required: true,
    },

    // 2. 概要（required: false, active: true）
    {
      key: 'description',
      label: '概要',
      accessor: (row) => row.description,
      render: (row) => {
        if (!row.description) return null
        const truncated = truncateText(row.description, 50)
        const isTruncated = row.description.length > 50

        if (!isTruncated) {
          return <span>{row.description}</span>
        }

        return (
          <AppTooltip content={<p className='max-w-sm'>{row.description}</p>}>
            <span className='cursor-help'>{truncated}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'w-[120px]',
      headerClassName: 'w-[120px]',
      active: true,
    },

    // 3. オポチュニティ管理ID（required: false, active: true）
    {
      key: 'opportunity_id',
      label: 'オポチュニティ管理ID',
      accessor: (row) => row.opportunity_id,
      render: (row) => (
        <Link
          to='/opportunities/$opportunityId'
          params={{ opportunityId: String(row.opportunity_id) }}
          search={{ tab: 'properties', fromPageLabel: '担当タスク一覧', fromPageLink: '/opportunities/tasks' }}
          target='_blank'
          rel='noopener noreferrer'
        >
          <AppBadgeForID value={row.opportunity_id} />
        </Link>
      ),
      cellClassName: 'w-[80px] text-center',
      headerClassName: 'w-[80px] text-center',
      active: true,
    },

    // 4. オポチュニティ（required: false, active: true）
    {
      key: 'opportunity_title',
      label: 'オポチュニティ',
      accessor: (row) => row.opportunity_title,
      cellClassName: 'w-[100px] text-center',
      headerClassName: 'w-[100px] text-center',
      active: true,
    },

    // 5. オポステータス（required: true）
    {
      key: 'opportunity_status_name',
      label: 'オポステータス',
      accessor: (row) => row.opportunity_status_name,
      render: (row) => {
        if (!row.opportunity_status_name) return '-'

        return (
          <AppBadge
            color={getColor(COLOR_CATEGORY.OPPORTUNITY_STATUS, row.opportunity_status_id)}
            className='whitespace-nowrap'
          >
            {row.opportunity_status_name}
          </AppBadge>
        )
      },
      cellClassName: 'w-[100px] text-center',
      headerClassName: 'w-[100px] text-center',
      required: true,
    },

    // 6. SF案件名（required: false, active: true）
    {
      key: 'salesforce_project_name',
      label: 'SF案件名',
      accessor: (row) => row.salesforce_project_name,
      cellClassName: 'w-[100px]',
      headerClassName: 'w-[100px]',
      active: true,
    },

    // 7. 担当者（required: false, active: true）
    {
      key: 'member_name',
      label: '担当者',
      accessor: (row) => row.member_name,
      render: (row) => <AppMemberDisplay employeeNumber={row.member_employee_number} name={row.member_name} />,
      cellClassName: 'w-[150px]',
      headerClassName: 'w-[150px]',
      active: true,
    },

    // 8. 期限（required: false, active: true）
    {
      key: 'expired_on',
      label: '期限',
      accessor: (row) => row.expired_on,
      render: (row) => toDateStringJP(row.expired_on),
      cellClassName: 'w-[100px] text-center',
      headerClassName: 'w-[100px] text-center',
      active: true,
    },

    // 9. ステータス（required: false, active: true）
    {
      key: 'opportunity_task_status_name',
      label: 'ステータス',
      accessor: (row) => row.opportunity_task_status_name,
      render: (row) => {
        const status = getTaskStatus(row.opportunity_task_status_id, statuses)
        if (!status) return row.opportunity_task_status_name

        return (
          <AppSelectPopover
            currentValue={String(row.opportunity_task_status_id)}
            options={statuses.map(
              (s): SelectOption => ({
                value: String(s.id),
                label: s.name,
                color: getColor(COLOR_CATEGORY.TASK_STATUS, s.id),
              }),
            )}
            onSelect={(value) => onStatusChange(row, Number(value))}
            triggerVariant='ghost'
            align='end'
          />
        )
      },
      cellClassName: 'w-[90px] text-center',
      headerClassName: 'w-[90px] text-center',
      active: true,
    },

    // 10. 更新者（required: false, active: false）
    {
      key: 'updated_by_name',
      label: '更新者',
      accessor: (row) => row.updated_by_name,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.updated_by_employee_number}
          name={row.updated_by_name}
          emptyDisplay='dash'
        />
      ),
      cellClassName: 'w-[150px]',
      headerClassName: 'w-[150px]',
      active: false,
    },

    // 11. 作成者（required: false, active: false）
    {
      key: 'created_by_name',
      label: '作成者',
      accessor: (row) => row.created_by_name,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.created_by_employee_number}
          name={row.created_by_name}
          emptyDisplay='dash'
        />
      ),
      cellClassName: 'w-[150px]',
      headerClassName: 'w-[150px]',
      active: false,
    },

    // 12. 更新日時（required: false, active: false）
    {
      key: 'updated_at',
      label: '更新日時',
      accessor: (row) => row.updated_at,
      render: (row) => {
        if (!row.updated_at) return '-'
        return (
          <AppTooltip content={toDateTime(row.updated_at)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(row.updated_at, 'relative')}</span>
          </AppTooltip>
        )
      },
      sortable: true,
      cellClassName: 'w-[70px]',
      headerClassName: 'w-[70px]',
      active: false,
    },

    // 13. 作成日時（required: false, active: false）
    {
      key: 'created_at',
      label: '作成日時',
      accessor: (row) => row.created_at,
      render: (row) => {
        if (!row.created_at) return '-'
        return (
          <AppTooltip content={toDateTime(row.created_at)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(row.created_at, 'relative')}</span>
          </AppTooltip>
        )
      },
      sortable: true,
      cellClassName: 'w-[70px]',
      headerClassName: 'w-[70px]',
      active: false,
    },

    // 14. Action（required: true, active: true）
    {
      key: 'action',
      label: 'Action',
      accessor: () => null,
      required: true,
      render: (row) => (
        <div className='flex items-center justify-center gap-2'>
          <AppIconButton onClick={() => onEdit(row)} description='タスクの編集'>
            <Pencil />
          </AppIconButton>

          <AppIconButton onClick={() => onDelete(row)} description='タスクの削除'>
            <Trash2 />
          </AppIconButton>
        </div>
      ),
      cellClassName: 'w-[80px] text-center',
      headerClassName: 'w-[80px] text-center',
      active: true,
    },
  ]
}
